import {toJS} from 'mobx';
import {applySnapshot, destroy, flow, types} from 'mobx-state-tree';
import CustomerModel from '../models/customer-model';
import CustomerService from '../services/customer-service';
import DeviceService from '../services/device-service';
import {getCurrentCustomerId} from 'src/helpers/customer';

const CustomerStore = types
    .model('CustomerStore', {
        customers: types.optional(types.array(CustomerModel), []),
        hasLoadedCustomers: false,
    })
    // O volatile guarda o valor como está, não a definição do tipo: a lista tem de nascer como lista.
    .volatile(() => ({
        selectedCustomer: null,
        selectedCustomerDevices: [],
    }))
    .actions((self) => {
        let isLoadCustomersRunning = false;

        return {
            loadCustomers: flow(function* (query) {
                if (self.hasLoadedCustomers || isLoadCustomersRunning) return;
                isLoadCustomersRunning = true;

                const customerService = new CustomerService();
                const _customers = yield customerService.list(query);

                const cleanedCustomers = _customers
                    .map((_customer) => ({
                        id: _customer.id,
                        groupId: _customer.groupId,
                        cpfCnpj: _customer.cpfCnpj,
                        type: _customer.type,
                        name: _customer.name,
                        street: _customer.street,
                        number: _customer.number,
                        complement: _customer.complement,
                        neighborhood: _customer.neighborhood,
                        city: _customer.city,
                        state: _customer.state,
                        postalCode: _customer.postalCode,
                        phone: _customer.phone,
                        cellphone: _customer.cellphone,
                        notificationEmail: _customer.notificationEmail,
                        secondaryEmail: _customer.secondaryEmail,
                        category: _customer.category,
                        customerId: _customer.customerId,
                        status: _customer.status,
                        notes: _customer.notes,
                        disabled: _customer.disabled,
                        registrationDate: _customer.registrationDate,
                        expirationDate: _customer.expirationDate,
                        invoiceDay: _customer.invoiceDay,
                        attributes: _customer.attributes,
                        expirationTime: _customer.expirationTime,
                        createdByUserId: _customer.createdByUserId,
                        createdAt: _customer.createdAt,
                        updatedAt: _customer.updatedAt,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                applySnapshot(self.customers, cleanedCustomers);

                self.hasLoadedCustomers = true;
                isLoadCustomersRunning = false;
            }),

            // TODO: check usage and remove in future
            setCustomers(customers) {
                self.customers = customers;
            },

            // TODO: check usage and remove in future
            addAll: (customers) => self.customers.push(...customers),

            addCustomer(data) {
                self.customers.push(data);
            },

            updateCustomer(customerId, data) {
                const _customer = self.findById(customerId);
                if (_customer) {
                    const {
                        // eslint-disable-next-line no-unused-vars
                        expirationTime, // ignored
                        // eslint-disable-next-line no-unused-vars
                        customerId: _customerId, // ignored
                        ...cleanedData
                    } = data;
                    applySnapshot(_customer, {
                        id: customerId,
                        expirationTime: _customer.expirationTime,
                        ...cleanedData,
                    });
                }
            },

            removeCustomer(customerId) {
                const _customer = self.findById(customerId);
                if (_customer) {
                    destroy(_customer);
                }
            },

            fetchCustomerDevices: flow(function* (customerId) {
                self.selectedCustomer = self.findById(customerId);
                if (!self.selectedCustomer) return;
                const deviceService = new DeviceService();
                self.selectedCustomerDevices = yield deviceService.getDevicesByCustomerId(self.selectedCustomer.id);
            }),

            cleanupCustomerDevices() {
                self.selectedCustomerDevices = [];
                self.selectedCustomer = null;
            },
        };
    })
    .views((self) => ({
        // Índice por id: o login do administrador liga cada dispositivo da frota inteira ao cliente e ao
        // dono, e a tela relê esses nomes a cada renderização. Como computed, ele é montado uma vez por
        // action ou reação, não a cada chamada.
        get customersById() {
            return new Map(self.customers.map((customer) => [customer.id, customer]));
        },
        findById: (id) => self.customersById.get(id),
        getCustomers() {
            return self.customers;
        },
        get list() {
            return toJS(self.customers);
        },
        get listAsOptions() {
            return toJS(self.customers.map((customer) => ({
                id: customer.id,
                title: customer.name,
            })));
        },
        get isCurrentCustomerMonitoring() {
            return this.findById(getCurrentCustomerId())?.category === 'monitoring';
        },
    }));

export default CustomerStore;

